import React, { useState } from 'react';
import { Observer } from 'mobx-react';
import {
    DialogTitle,
    TextField,
    Chip,
    Box,
    Checkbox,
    FormControlLabel,
    Typography,
    IconButton,
    MenuItem,
    Select,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControl,
    Divider,
    Dialog,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Tooltip,
    Paper,
    alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { useProcedureUIStore } from '@core/useStores';
import { messages } from 'Procedures/procedures.messages';
import { AnyError } from 'Procedures/domain/ProcedureError';
import { toUserFriendlyString } from '@shared/helpers/stringUtils';
import { ThemeProps } from '@styles/theme';
import { useStyles } from './ScoreDataModal.styles';
import { Modal } from '@shared/components/Modal';
import {
    AddCircle, Backspace, ExpandMore,
    ScoreOutlined, ErrorOutline, DataObject, TuneOutlined,
    CheckCircleOutline, BlockOutlined, RepeatOutlined,
} from '@mui/icons-material';
import { ScoreVariable } from 'Procedures/domain/Device';
import { Button } from '@shared/components/Button';

// ── helpers ──────────────────────────────────────────────────────────────────

function repeatablePointsToString(pts: { ErrorNumber: number; PointsValue: number }[]): string {
    if (!pts || pts.length === 0) {return '';}
    const maxIdx = Math.max(...pts.map(p => p.ErrorNumber), 0);
    const arr = new Array(maxIdx + 1).fill(0);
    pts.forEach(({ ErrorNumber, PointsValue }) => { arr[ErrorNumber] = PointsValue; });

    return arr.join(', ');
}

function stringToRepeatablePoints(s: string): { ErrorNumber: number; PointsValue: number }[] {
    return s.split(',')
        .map((v, i) => ({ ErrorNumber: i, PointsValue: parseInt(v.trim(), 10) || 0 }))
        .filter((p, i, arr) => p.PointsValue > 0 || i === arr.length - 1);
}

// ── Error type color ─────────────────────────────────────────────────────────

const ERROR_TYPE_COLORS: Record<string, string> = {
    GrabError: '#6B5EA7',
    TimingError: '#3D7EC4',
    ProcedureError: '#50AE8C',
};

function errorTypeColor(type: string): string {
    return ERROR_TYPE_COLORS[type] ?? '#888';
}

// ── NewVariableDialog ─────────────────────────────────────────────────────────

interface NewVariableDialogProps {
  open: boolean;
  onClose: (name: string, type: string) => void;
}

function NewVariableDialog({ onClose, open }: NewVariableDialogProps) {
    const [newVariable, setNewVariable] = useState('');

    return (
        <Dialog onClose={() => onClose('', '')} open={open} PaperProps={{ sx: { borderRadius: 2, minWidth: 320 } }}>
            <DialogTitle sx={{ pb: 0.5 }}>
                <Typography variant="h6" color="primary">{messages['scoreDataModal.variables.newVariable.dialog.title']}</Typography>
            </DialogTitle>
            <List sx={{ pt: 0, px: 2, pb: 2 }}>
                <ListItem sx={{ px: 0, pb: 1 }}>
                    <TextField
                        label={messages['scoreDataModal.variables.newVariable.dialog.nameField']}
                        size="small"
                        autoFocus
                        onChange={e => setNewVariable(e.target.value)}
                        fullWidth
                        slotProps={{ inputLabel: { shrink: true } }}
                    />
                </ListItem>
                <Typography variant="caption" color="textSecondary" sx={{ pl: 0.5 }}>Select type:</Typography>
                {['Integer', 'Bool', 'Float', 'String', 'StringFormat', 'Time'].map(v => (
                    <ListItem disablePadding key={v}>
                        <ListItemButton
                            disabled={newVariable === ''}
                            onClick={() => onClose(newVariable, v)}
                            sx={{ borderRadius: 1, py: 0.5 }}
                        >
                            <ListItemText primary={v} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        </Dialog>
    );
}

// ── Variable value editor ────────────────────────────────────────────────────

const VariableValueInput: React.FC<{
  variable: ScoreVariable;
  onChange: (field: 'string' | 'generic' | 'object', value: any) => void;
}> = ({ variable, onChange }) => {
    if (variable.type === 'Bool') {
        return (
            <Checkbox
                size="small"
                checked={variable.generic.x > 0}
                onChange={e => onChange('generic', { ...variable.generic, x: e.target.checked ? 1 : 0 })}
                sx={{ p: 0.5 }}
            />
        );
    }
    if (variable.type === 'Float' || variable.type === 'Integer') {
        return (
            <TextField
                type="number"
                size="small"
                value={variable.generic?.x ?? 0}
                onChange={e => onChange('generic', { ...variable.generic, x: parseFloat(e.target.value) })}
                sx={{ width: 120 }}
                slotProps={{ htmlInput: { step: variable.type === 'Float' ? '0.1' : '1' } }}
            />
        );
    }
    if (variable.type === 'String' || variable.type === 'StringFormat') {
        return (
            <TextField
                value={variable.string}
                size="small"
                multiline
                onChange={e => onChange('string', e.target.value)}
                sx={{ flex: 1, minWidth: 120 }}
            />
        );
    }
    if (variable.type === 'Time') {
        return (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                        value={variable.generic?.x > 0 ? 'Timer' : 'Stopwatch'}
                        onChange={e => onChange('generic', { ...variable.generic, x: e.target.value === 'Timer' ? 1 : 0 })}
                    >
                        {['Stopwatch', 'Timer'].map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                    </Select>
                </FormControl>
                <TextField
                    type="number"
                    value={variable.generic?.y ?? 0}
                    onChange={e => onChange('generic', { ...variable.generic, y: parseFloat(e.target.value) })}
                    size="small"
                    sx={{ width: 90 }}
                    slotProps={{
                        input: {
                            endAdornment: <InputAdornment position="end"><Typography variant="caption">s</Typography></InputAdornment>,
                        },
                    }}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={variable.generic?.z > 0}
                            onChange={e => onChange('generic', { ...variable.generic, z: e.target.checked ? 1 : 0 })}
                            size="small"
                        />
                    }
                    label={<Typography variant="caption">Repeat</Typography>}
                    sx={{ m: 0 }}
                />
            </Box>
        );
    }

    return (
        <TextField
            value={variable.object}
            size="small"
            onChange={e => onChange('object', e.target.value)}
            sx={{ flex: 1, minWidth: 120 }}
        />
    );
};

// ── ChildPropertyRow ─────────────────────────────────────────────────────────

interface ChildPropertyRowProps {
  label: string;
  value: any;
  error: AnyError;
  onChange: (value: any) => void;
}

const ChildPropertyRow = ({ label, value, error, onChange }: ChildPropertyRowProps) => {
    const [newTag, setNewTag] = useState('');
    const isBoolean = typeof value === 'boolean';
    const isNumber = typeof value === 'number';

    const renderInput = () => {
        if (label === 'tags') {
            return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                    {(value as string[])?.map((tag, idx) => (
                        <Chip
                            key={idx}
                            label={tag}
                            size="small"
                            onDelete={() => onChange((value as string[]).filter((_, i) => i !== idx))}
                        />
                    ))}
                    <TextField
                        value={newTag}
                        onChange={e => setNewTag(e.target.value)}
                        onKeyUp={e => {
                            if (e.key === 'Enter' && newTag.trim()) {
                                onChange(value ? [...value, newTag.trim()] : [newTag.trim()]);
                                setNewTag('');
                            }
                        }}
                        placeholder="Add tag…"
                        size="small"
                        sx={{ width: 110 }}
                    />
                </Box>
            );
        }
        if (error.type === 'GrabError' && label === 'grabber') {
            return (
                <Select value={value} onChange={e => onChange(e.target.value)} size="small" fullWidth>
                    {['Anything', 'Cursor', 'LeftHand', 'RightHand', 'BothHands', 'AnyHand'].map(o => (
                        <MenuItem key={o} value={o}>{o}</MenuItem>
                    ))}
                </Select>
            );
        }
        if (error.type === 'TimingError' && label === 'countdown') {
            return (
                <TextField type="number" value={value} onChange={e => onChange(Number(e.target.value))} size="small" fullWidth />
            );
        }
        if (isBoolean) {
            return <Checkbox checked={value} onChange={e => onChange(e.target.checked)} size="small" />;
        }

        return (
            <TextField
                fullWidth
                value={value}
                onChange={e => onChange(isNumber ? Number(e.target.value) : e.target.value)}
                size="small"
                type={isNumber ? 'number' : 'text'}
                multiline={label === 'description'}
            />
        );
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.5 }}>
            <Typography sx={{ width: 120, flexShrink: 0, pt: 0.75, color: 'text.secondary', fontSize: 12 }}>
                {toUserFriendlyString(label)}
            </Typography>
            <Box sx={{ flexGrow: 1, '& .MuiInputBase-input': { fontSize: 13 }, '& .MuiSelect-select': { fontSize: 13 } }}>{renderInput()}</Box>
        </Box>
    );
};

// ── ErrorCard ────────────────────────────────────────────────────────────────

interface ErrorCardProps {
  index: number;
  error: AnyError;
  onDelete: () => void;
  onChange: (index: number, key: string, value: any) => void;
}

const HEADER_KEYS = new Set(['_ref', 'id', 'active', 'type', 'points', 'blocking', 'repeatable', 'feedbackObjects', 'repeatablePoints', 'steps', 'tags', 'description']);

const ErrorCard: React.FC<ErrorCardProps> = ({ index, error, onDelete, onChange }) => {
    if (!error.repeatablePoints || error.repeatablePoints.length === 0 || error.repeatablePoints[0] === undefined) {
        error.repeatablePoints = [{ ErrorNumber: 0, PointsValue: error.points }];
    }

    const typeColor = errorTypeColor(error.type);
    const childProps = Object.entries(error).filter(([key]) => !HEADER_KEYS.has(key));

    return (
        <Accordion
            defaultExpanded={false}
            disableGutters
            elevation={0}
            sx={{
                border: `1px solid`,
                borderColor: alpha(typeColor, 0.3),
                borderRadius: '8px !important',
                '&:before': { display: 'none' },
                '&.Mui-expanded': { borderColor: typeColor },
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMore sx={{ fontSize: 16 }} />}
                sx={{
                    minHeight: 42,
                    px: 1.5,
                    '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, my: 0.5 },
                    backgroundColor: alpha(typeColor, 0.04),
                    borderRadius: '8px',
                    '&.Mui-expanded': { borderRadius: '8px 8px 0 0', borderBottom: `1px solid ${alpha(typeColor, 0.2)}` },
                }}
            >
                {/* Error ID badge */}
                <Box sx={{
                    minWidth: 30, height: 23, borderRadius: 1,
                    bgcolor: typeColor, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 800, color: 'white', lineHeight: 1 }}>
                        {error.id}
                    </Typography>
                </Box>

                {/* Type chip */}
                <Chip
                    label={toUserFriendlyString(error.type)}
                    size="small"
                    sx={{
                        height: 21, fontSize: 11,
                        bgcolor: alpha(typeColor, 0.12), color: typeColor,
                        border: `1px solid ${alpha(typeColor, 0.25)}`,
                        fontWeight: 600,
                        '& .MuiChip-label': { px: 0.75 },
                    }}
                />

                {/* Points input */}
                <TextField
                    value={repeatablePointsToString(error.repeatablePoints)}
                    onClick={e => e.stopPropagation()}
                    onChange={e => {
                        const pts = stringToRepeatablePoints(e.target.value);
                        onChange(index, 'points', pts[0]?.PointsValue ?? 0);
                        onChange(index, 'repeatablePoints', pts);
                    }}
                    size="small"
                    placeholder="pts, comma-sep"
                    sx={{ width: 150, '& .MuiInputBase-input': { fontSize: 13 } }}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Typography sx={{ fontSize: 12 }} color="textSecondary">pts</Typography>
                                </InputAdornment>
                            ),
                        },
                    }}
                />

                {/* Flag icons (clickable, no expand trigger) */}
                <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', mr: 0.5 }}>
                    <Tooltip title={error.active ? 'Active (click to deactivate)' : 'Inactive (click to activate)'}>
                        <Box
                            component="span"
                            onClick={e => { e.stopPropagation(); onChange(index, 'active', !error.active); }}
                            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        >
                            <CheckCircleOutline sx={{ fontSize: 16, color: error.active ? 'success.main' : 'text.disabled' }} />
                        </Box>
                    </Tooltip>
                    <Tooltip title={error.blocking ? 'Blocking (click to unblock)' : 'Non-blocking (click to block)'}>
                        <Box
                            component="span"
                            onClick={e => { e.stopPropagation(); onChange(index, 'blocking', !error.blocking); }}
                            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        >
                            <BlockOutlined sx={{ fontSize: 16, color: error.blocking ? 'error.main' : 'text.disabled' }} />
                        </Box>
                    </Tooltip>
                    <Tooltip title={error.repeatable ? 'Repeatable (click to disable)' : 'Not repeatable (click to enable)'}>
                        <Box
                            component="span"
                            onClick={e => { e.stopPropagation(); onChange(index, 'repeatable', !error.repeatable); }}
                            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        >
                            <RepeatOutlined sx={{ fontSize: 16, color: error.repeatable ? 'primary.main' : 'text.disabled' }} />
                        </Box>
                    </Tooltip>
                    {error.feedbackObjects?.length > 0 && (
                        <Chip label={`${error.feedbackObjects.length} fb`} size="small"
                            sx={{ height: 17, fontSize: 9, '& .MuiChip-label': { px: 0.5 } }} />
                    )}
                </Box>

                {/* Delete */}
                <Tooltip title="Delete error">
                    <IconButton
                        size="small"
                        color="error"
                        onClick={e => { e.stopPropagation(); onDelete(); }}
                        sx={{ p: 0.25, flexShrink: 0 }}
                    >
                        <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                </Tooltip>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <ChildPropertyRow label="tags" value={error.tags} error={error} onChange={v => onChange(index, 'tags', v)} />
                <ChildPropertyRow label="description" value={error.description} error={error} onChange={v => onChange(index, 'description', v)} />
                <ChildPropertyRow label="steps" value={error.steps} error={error} onChange={v => onChange(index, 'steps', v)} />
                {childProps.length > 0 && (
                    <>
                        <Divider sx={{ my: 0.5 }} />
                        {childProps.map(([key, value]) => (
                            <ChildPropertyRow key={key} label={key} value={value} error={error} onChange={v => onChange(index, key, v)} />
                        ))}
                    </>
                )}
            </AccordionDetails>
        </Accordion>
    );
};

// ── SectionCard ──────────────────────────────────────────────────────────────

const SectionCard: React.FC<{
  icon: React.ReactNode;
  title: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  noPad?: boolean;
}> = ({ icon, title, action, children, noPad }) => {
    const theme = useTheme<ThemeProps>();

    return (
        <Paper elevation={0} sx={{
            border: `1px solid ${theme.colors.grey_light}`,
            borderRadius: 2,
            overflow: 'hidden',
            '&::before': {
                content: '""', display: 'block', height: 3,
                background: `linear-gradient(90deg, ${theme.colors.violet_primary}, ${theme.colors.blue_primary})`,
            },
        }}>
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 2, py: 1.25,
                borderBottom: `1px solid ${theme.colors.grey_light}`,
                backgroundColor: theme.colors.transp_violet,
            }}>
                <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>{icon}</Box>
                <Typography variant="subtitle2" color="primary"
                    sx={{ fontWeight: 700, fontSize: 13, letterSpacing: '0.04em', textTransform: 'uppercase', flex: 1 }}>
                    {title}
                </Typography>
                {action}
            </Box>
            <Box sx={{ p: noPad ? 0 : 2 }}>{children}</Box>
        </Paper>
    );
};

// ── ScoreDataPanel ───────────────────────────────────────────────────────────

export const ScoreDataPanel: React.FC<{ onClose?: () => void; embedded?: boolean }> = ({ onClose, embedded }) => {
    const theme = useTheme<ThemeProps>();
    const procedureUIStore = useProcedureUIStore();
    const [showNewVariable, setShowNewVariable] = useState(false);

    return (
        <Observer>
            {() => {
                const handleErrorChange = (index: number, key: string, value: any) => {
                    const newErrors = procedureUIStore.scoreData?.errors.map((error, idx) =>
                        idx === index ? { ...error, [key]: value } : error
                    );
                    procedureUIStore.updateScoreData({ errors: newErrors });
                };

                const handleDeleteError = (index: number) => {
                    const newErrors = procedureUIStore.scoreData?.errors.filter((_, idx) => idx !== index);
                    procedureUIStore.updateScoreData({ errors: newErrors });
                };

                const handleAddNewVariable = (name: string, type: string) => {
                    if (!name || !type) {
                        setShowNewVariable(false);

                        return;
                    }
                    const newVar: ScoreVariable = {
                        name, type: type ?? 'Any',
                        generic: { x: 0, y: 0, z: 0, w: 0 },
                        string: '', object: '',
                    };
                    const variables = procedureUIStore.scoreData?.variables ?? [];
                    procedureUIStore.updateScoreData({ variables: [newVar, ...variables] });
                    setShowNewVariable(false);
                };

                const handleDeleteVariable = (index: number) => {
                    const newVariables = [...procedureUIStore.scoreData!.variables];
                    newVariables.splice(index, 1);
                    procedureUIStore.updateScoreData({ variables: newVariables });
                };

                if (!procedureUIStore.scoreData) {
                    return (
                        <Box sx={{ p: 3 }}>
                            <Typography color="textSecondary">No scoring data available.</Typography>
                        </Box>
                    );
                }

                const sd = procedureUIStore.scoreData;

                return (
                    <>
                        <NewVariableDialog open={showNewVariable} onClose={handleAddNewVariable} />

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: embedded ? 1.5 : 2 }}>

                            {/* ── Scoring Parameters ─────────────────────────────── */}
                            <SectionCard
                                icon={<TuneOutlined sx={{ fontSize: 16 }} />}
                                title={messages['scoreDataModal.scoringParameters.label']}
                            >
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 1.5 }}>
                                    <TextField
                                        label={messages['scoreDataModal.maxScore.label']}
                                        type="number" size="small"
                                        value={sd.maxScore}
                                        onChange={e => procedureUIStore.updateScoreData({ maxScore: parseFloat(e.target.value) })}
                                        fullWidth
                                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: '0.1' } }}
                                    />
                                    <TextField
                                        label={messages['scoreDataModal.passScore.label']}
                                        type="number" size="small"
                                        value={sd.passScore}
                                        onChange={e => procedureUIStore.updateScoreData({ passScore: parseFloat(e.target.value) })}
                                        fullWidth
                                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: '0.1' } }}
                                    />
                                    <TextField
                                        label={messages['scoreDataModal.stepPoints.label']}
                                        type="number" size="small"
                                        value={sd.stepPoints}
                                        onChange={e => procedureUIStore.updateScoreData({ stepPoints: parseFloat(e.target.value) })}
                                        fullWidth
                                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: '0.1' } }}
                                    />
                                </Box>

                                {sd.stepWeights && Object.keys(sd.stepWeights).length > 0 && (
                                    <>
                                        <Divider sx={{ my: 1.5 }} />
                                        <Typography sx={{ fontSize: 12, fontWeight: 600, display: 'block', mb: 1, color: 'text.secondary' }}>
                                            {messages['scoreDataModal.stepWeights.label']}
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {Object.entries(sd.stepWeights).map(([step, weight]) => (
                                                <Box key={step} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <Typography variant="caption" sx={{ minWidth: 60, color: 'text.secondary' }}>{step}</Typography>
                                                    <TextField
                                                        value={weight}
                                                        onChange={e => procedureUIStore.updateScoreData({
                                                            stepWeights: { ...sd.stepWeights, [step]: Number(e.target.value) },
                                                        })}
                                                        type="number" size="small" sx={{ width: 80 }}
                                                    />
                                                </Box>
                                            ))}
                                        </Box>
                                    </>
                                )}
                            </SectionCard>

                            {/* ── Execution Modes ────────────────────────────────── */}
                            <SectionCard
                                icon={<ScoreOutlined sx={{ fontSize: 16 }} />}
                                title={messages['scoreDataModal.executionModes.label']}
                            >
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                    {sd.executionModes.map(mode => (
                                        <Chip key={mode} label={mode} size="small" variant="outlined" color="primary"
                                            sx={{ height: 22, fontSize: 11 }} />
                                    ))}
                                </Box>
                            </SectionCard>

                            {/* ── Variables ──────────────────────────────────────── */}
                            <SectionCard
                                icon={<DataObject sx={{ fontSize: 16 }} />}
                                title={
                                    <>
                                        {messages['scoreDataModal.variables.label']}
                                        <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 0.5 }}>
                      ({sd.variables?.length ?? 0})
                                        </Typography>
                                    </>
                                }
                                action={
                                    <Button
                                        size="small"
                                        color="primary"
                                        variant="contained"
                                        startIcon={<AddCircle sx={{ fontSize: 14 }} />}
                                        onClick={() => setShowNewVariable(true)}
                                        sx={{ py: 0.25, px: 1, fontSize: 11, minWidth: 0 }}
                                    >
                                        {messages['scoreDataModal.variables.newVariable']}
                                    </Button>
                                }
                                noPad
                            >
                                {(!sd.variables || sd.variables.length === 0) ? (
                                    <Box sx={{ p: 2 }}>
                                        <Typography variant="caption" color="textSecondary">No variables defined.</Typography>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Box sx={{
                                            display: 'grid', gridTemplateColumns: '1fr 80px 1fr 36px',
                                            gap: 1, px: 2, py: 0.75,
                                            backgroundColor: theme.colors.grey_background,
                                            borderBottom: `1px solid ${theme.colors.grey_light}`,
                                        }}>
                                            {['NAME', 'TYPE', 'VALUE', ''].map((h, i) => (
                                                <Typography key={i} sx={{ fontWeight: 600, fontSize: 11, color: 'text.secondary' }}>{h}</Typography>
                                            ))}
                                        </Box>
                                        {sd.variables.map((variable, index) => {
                                            const handleVariableChange = (field: 'string' | 'generic' | 'object', newValue: any) => {
                                                const newVariables = [...sd.variables];
                                                newVariables[index] = { ...variable, [field]: newValue };
                                                procedureUIStore.updateScoreData({ variables: newVariables });
                                            };

                                            return (
                                                <Box
                                                    key={variable.name}
                                                    sx={{
                                                        display: 'grid', gridTemplateColumns: '1fr 80px 1fr 36px',
                                                        gap: 1, px: 2, py: 0.75, alignItems: 'center',
                                                        backgroundColor: index % 2 === 1 ? theme.colors.transp_violet : 'transparent',
                                                        borderBottom: `1px solid ${theme.colors.grey_lighter}`,
                                                        '&:last-child': { borderBottom: 'none' },
                                                    }}
                                                >
                                                    <Typography sx={{ fontSize: 13, fontWeight: 500, wordBreak: 'break-all' }}>
                                                        {variable.name}
                                                    </Typography>
                                                    <Chip label={variable.type} size="small" sx={{
                                                        height: 19, fontSize: 10, width: 'fit-content',
                                                        fontFamily: 'monospace', fontWeight: 600,
                                                        '& .MuiChip-label': { px: 0.75 },
                                                    }} />
                                                    <VariableValueInput variable={variable} onChange={handleVariableChange} />
                                                    <Tooltip title="Delete variable">
                                                        <IconButton size="small" color="error" onClick={() => handleDeleteVariable(index)} sx={{ p: 0.5 }}>
                                                            <Backspace sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}
                            </SectionCard>

                            {/* ── Errors ─────────────────────────────────────────── */}
                            <SectionCard
                                icon={<ErrorOutline sx={{ fontSize: 16 }} />}
                                title={
                                    <>
                                        {messages['scoreDataModal.errors.label']}
                                        <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 0.5 }}>
                      ({sd.errors.length})
                                        </Typography>
                                    </>
                                }
                                noPad
                            >
                                {sd.errors.length === 0 ? (
                                    <Box sx={{ p: 2 }}>
                                        <Typography variant="caption" color="textSecondary">No errors defined.</Typography>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, p: 1.5 }}>
                                        {sd.errors.map((error, index) => (
                                            <ErrorCard
                                                key={index}
                                                index={index}
                                                error={error}
                                                onDelete={() => handleDeleteError(index)}
                                                onChange={handleErrorChange}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </SectionCard>

                        </Box>

                        {!embedded && (
                            <Box sx={{
                                display: 'flex', justifyContent: 'flex-end', gap: 1.5,
                                borderTop: `1px solid ${theme.colors.grey_light}`,
                                px: 2, py: 1.5,
                            }}>
                                {onClose && (
                                    <Button color="secondary" onClick={onClose}>{messages['procedureModal.cancel']}</Button>
                                )}
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={async () => {
                                        await procedureUIStore.saveScoreData();
                                        onClose?.();
                                    }}
                                >
                                    {messages['procedureModal.save']}
                                </Button>
                            </Box>
                        )}
                    </>
                );
            }}
        </Observer>
    );
};

// ── ScoreDataModal (standalone wrapper) ──────────────────────────────────────

export const ScoreDataModal = () => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const procedureUIStore = useProcedureUIStore();

    return (
        <Modal
            isOpen={procedureUIStore.scoreDataModalOpened}
            onRequestClose={() => procedureUIStore.toggleScoreDataModalOpen(false)}
            className={styles.modal}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc
        >
            <Box sx={{ px: 2, pt: 2, pb: 1, borderBottom: `1px solid ${theme.colors.grey_light}` }}>
                <Typography variant="h5" color="primary" sx={{ fontWeight: 700 }}>
                    {messages['scoreDataModal.title']}
                </Typography>
            </Box>
            <Box sx={{ maxHeight: 'calc(100vh - 160px)', overflowY: 'auto' }}>
                <ScoreDataPanel onClose={() => procedureUIStore.toggleScoreDataModalOpen(false)} />
            </Box>
        </Modal>
    );
};

